import React from "react";

import {
  mdiAlertCircleOutline,
  mdiCheckCircleOutline,
  mdiCircleOutline,
  mdiDotsCircle,
  mdiLoading,
} from "@mdi/js";
import Icon, { Stack } from "@mdi/react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import type { EncodingStatus } from "../../../api/EncoderAPI.types";

export const iconByStreamStatus: Record<
  Exclude<EncodingStatus, "transcoding">,
  string
> = {
  transcoded: mdiCheckCircleOutline,
  error: mdiAlertCircleOutline,
  canceled: mdiAlertCircleOutline,
  untranscoded: mdiDotsCircle,
  queued: mdiDotsCircle,
};
export const classNamesByStatus: Record<EncodingStatus, string> = {
  transcoded: "text-status-transcoded",
  error: "text-status-error",
  untranscoded: "text-status-untranscoded",
  queued: "text-status-queued animate-pulse",
  canceled: "text-muted-foreground",
  transcoding: "text-status-transcoding",
};

interface StatusBadgeProps {
  status: EncodingStatus;
  progression?: number | null;
}

export default function StatusBadge({
  status,
  progression,
}: StatusBadgeProps): React.JSX.Element {
  const { t } = useTranslation("editor-content");

  const statusClassNames = classnames(
    "flex items-center gap-1.5 text-[0.78125rem].78125rem]",
    classNamesByStatus[status],
  );

  const isEncoding = status === "transcoding";

  return (
    <div className="flex flex-col items-start">
      <div className={statusClassNames}>
        {isEncoding ? (
          <span className="text-status-transcoding text-[0.78125rem]">
            {progression && progression > 0
              ? `${progression}%`
              : t(`streamStatus.${status}`)}
          </span>
        ) : (
          <span>{t(`streamStatus.${status}`)}</span>
        )}
        {isEncoding ? (
          <Stack className="animate-spin" size={0.625}>
            <Icon path={mdiCircleOutline} />
            <Icon className="text-card" path={mdiLoading} />
          </Stack>
        ) : (
          <Icon path={iconByStreamStatus[status]} size={0.625} />
        )}
      </div>
    </div>
  );
}
